import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, FileDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { RouteErrorBoundary } from "@/components/medam/route-error-boundary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ESTATUS_POSTULACION,
  ETIQUETA_ESTATUS_POSTULACION,
  formatearFechaHora,
  type EstatusPostulacion,
} from "@/lib/catalogos";
import {
  actualizarPostulacionAdmin,
  agregarNotaAdmin,
  obtenerPostulacionAdmin,
  urlDocumentoAdmin,
} from "@/lib/postulaciones.functions";

export const Route = createFileRoute("/_authenticated/admin/postulaciones/$id")({
  errorComponent: ({ error, reset }) => (
    <RouteErrorBoundary
      error={error}
      reset={reset}
      titulo="No pudimos cargar la postulación"
      descripcion="Ocurrió un problema al abrir el detalle de la postulación."
    />
  ),
  component: DetallePostulacion,
});

const aLista = (texto: string) =>
  texto
    .split("\n")
    .map((linea) => linea.trim())
    .filter(Boolean);

const sonListasIguales = (a: string[], b: string[]) =>
  a.length === b.length && a.every((valor, indice) => valor === b[indice]);

function DetallePostulacion() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: ["admin", "postulacion", id],
    queryFn: () => obtenerPostulacionAdmin({ id }),
  });

  const [estatus, setEstatus] = useState<EstatusPostulacion>("nueva");
  const [calificacion, setCalificacion] = useState("");
  const [fortalezas, setFortalezas] = useState("");
  const [aspectos, setAspectos] = useState("");
  const [comentario, setComentario] = useState("");
  const [nota, setNota] = useState("");
  const idInicializadoRef = useRef<string | null>(null);

  const postulacion = consulta.data?.postulacion;

  useEffect(() => {
    if (!postulacion) return;
    if (idInicializadoRef.current !== postulacion.id) {
      setEstatus(postulacion.estatus as EstatusPostulacion);
      setCalificacion(postulacion.calificacion ? String(postulacion.calificacion) : "");
      setFortalezas((postulacion.fortalezas ?? []).join("\n"));
      setAspectos((postulacion.aspectos_validar ?? []).join("\n"));
      idInicializadoRef.current = postulacion.id;
      return;
    }

    const estatusRemoto = postulacion.estatus as EstatusPostulacion;
    const calificacionRemota = postulacion.calificacion ? String(postulacion.calificacion) : "";
    const fortalezasRemotas = postulacion.fortalezas ?? [];
    const aspectosRemotos = postulacion.aspectos_validar ?? [];

    setEstatus((previo) => (previo === estatusRemoto ? previo : estatusRemoto));
    setCalificacion((previo) => (previo === calificacionRemota ? previo : calificacionRemota));
    setFortalezas((previo) => {
      const listaPrevia = aLista(previo);
      return sonListasIguales(listaPrevia, fortalezasRemotas) ? previo : fortalezasRemotas.join("\n");
    });
    setAspectos((previo) => {
      const listaPrevia = aLista(previo);
      return sonListasIguales(listaPrevia, aspectosRemotos) ? previo : aspectosRemotos.join("\n");
    });
  }, [postulacion]);

  const clavesInvalidacion = useMemo(
    () => [
      ["admin", "postulacion", id] as const,
      ["admin", "postulaciones"] as const,
      ["admin", "resumen"] as const,
    ],
    [id],
  );

  const refrescar = useCallback(() => {
    for (const queryKey of clavesInvalidacion) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [clavesInvalidacion, queryClient]);

  const guardar = useMutation({
    mutationFn: () =>
      actualizarPostulacionAdmin({
        id,
        estatus,
        estatusAnterior: postulacion?.estatus ?? null,
        calificacion: calificacion ? Number(calificacion) : null,
        fortalezas: aLista(fortalezas),
        aspectos_validar: aLista(aspectos),
        comentario: comentario.trim() || null,
      }),
    onSuccess: () => {
      setComentario("");
      refrescar();
      toast.success("Evaluación guardada.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const nuevaNota = useMutation({
    mutationFn: () => agregarNotaAdmin({ postulacion_id: id, nota }),
    onSuccess: () => {
      setNota("");
      refrescar();
      toast.success("Nota agregada.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const abrirDocumento = async (ruta: string) => {
    try {
      const { url } = await urlDocumentoAdmin({ ruta });
      window.open(url, "_blank", "noopener");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No pudimos abrir el documento.");
    }
  };

  if (consulta.isLoading) return <Loader2 className="size-6 animate-spin text-primary" />;
  if (consulta.error)
    return <p className="text-sm text-destructive">{(consulta.error as Error).message}</p>;
  if (!postulacion) return <p className="text-sm text-muted-foreground">Postulación no encontrada.</p>;

  const persona = postulacion.postulante;

  return (
    <div className="space-y-6">
      <Link
        to="/admin/postulaciones/"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Volver a postulaciones
      </Link>

      <header className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl">{persona?.nombre_completo}</h1>
          <Badge variant="secondary">
            {ETIQUETA_ESTATUS_POSTULACION[postulacion.estatus as EstatusPostulacion]}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Folio {postulacion.folio} · {postulacion.posiciones?.nombre} ·{" "}
          {formatearFechaHora(postulacion.created_at)}
        </p>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Dato etiqueta="Correo" valor={persona?.correo} />
          <Dato etiqueta="Teléfono" valor={persona?.telefono} />
          <Dato etiqueta="Municipio" valor={persona?.municipio} />
          <Dato etiqueta="Escolaridad" valor={persona?.escolaridad} />
          <Dato etiqueta="Carrera" valor={persona?.carrera} />
          <Dato etiqueta="Institución" valor={persona?.institucion} />
          <Dato etiqueta="Último puesto" valor={persona?.ultimo_puesto} />
          <Dato etiqueta="Años de experiencia" valor={String(persona?.anios_experiencia ?? "")} />
          <Dato etiqueta="Área de experiencia" valor={persona?.area_experiencia} />
          <Dato etiqueta="Cédula profesional" valor={persona?.cedula_profesional} />
          <Dato etiqueta="Turno de preferencia" valor={postulacion.turno_preferencia} />
          <Dato etiqueta="Otro turno" valor={postulacion.disponibilidad_otro_turno} />
          <Dato etiqueta="Disponibilidad horario" valor={persona?.disponibilidad_horario} />
          <Dato etiqueta="Capacitación previa" valor={persona?.disponibilidad_capacitacion} />
          <Dato etiqueta="Segunda opción" valor={postulacion.segunda?.nombre} />
        </dl>
        {persona?.certificaciones ? (
          <p className="mt-4 whitespace-pre-line text-sm text-muted-foreground">
            <strong className="text-foreground">Certificaciones: </strong>
            {persona.certificaciones}
          </p>
        ) : null}
      </header>

      <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <h2 className="font-display text-lg">Respuestas</h2>
        <ol className="mt-4 space-y-5">
          {consulta.data?.respuestas.map((respuesta) => (
            <li key={respuesta.id}>
              <p className="text-sm font-medium">{respuesta.pregunta}</p>
              <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                {respuesta.respuesta}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <h2 className="font-display text-lg">Documentos</h2>
        {consulta.data?.documentos.length ? (
          <ul className="mt-4 space-y-2">
            {consulta.data.documentos.map((documento) => (
              <li key={documento.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate">
                  {documento.tipo}: {documento.nombre_archivo}
                </span>
                <Button size="sm" variant="outline" onClick={() => abrirDocumento(documento.ruta)}>
                  <FileDown className="mr-1 size-4" /> Abrir
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">Sin documentos adjuntos.</p>
        )}
      </section>

      <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <h2 className="font-display text-lg">Evaluación interna</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Estatus</Label>
            <Select value={estatus} onValueChange={(valor) => setEstatus(valor as EstatusPostulacion)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTATUS_POSTULACION.map((valor) => (
                  <SelectItem key={valor} value={valor}>
                    {ETIQUETA_ESTATUS_POSTULACION[valor]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Calificación general (1 a 5)</Label>
            <Select value={calificacion} onValueChange={setCalificacion}>
              <SelectTrigger>
                <SelectValue placeholder="Sin calificar" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((valor) => (
                  <SelectItem key={valor} value={String(valor)}>
                    {valor}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Fortalezas (una por línea)</Label>
            <Textarea
              rows={4}
              value={fortalezas}
              onChange={(evento) => setFortalezas(evento.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Aspectos a validar (uno por línea)</Label>
            <Textarea
              rows={4}
              value={aspectos}
              onChange={(evento) => setAspectos(evento.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Comentario del cambio de estatus (opcional)</Label>
            <Textarea
              rows={2}
              value={comentario}
              onChange={(evento) => setComentario(evento.target.value)}
            />
          </div>
        </div>
        <Button className="mt-4" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
          {guardar.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Guardar evaluación
        </Button>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
          <h2 className="font-display text-lg">Notas internas</h2>
          <Textarea
            className="mt-3"
            rows={3}
            placeholder="Escribe una nota para el equipo"
            value={nota}
            onChange={(evento) => setNota(evento.target.value)}
          />
          <Button
            className="mt-3"
            size="sm"
            onClick={() => nuevaNota.mutate()}
            disabled={nuevaNota.isPending || nota.trim().length < 3}
          >
            Agregar nota
          </Button>
          <ul className="mt-5 space-y-3">
            {consulta.data?.notas.map((registro) => (
              <li key={registro.id} className="rounded-xl border border-border/70 p-3 text-sm">
                <p className="whitespace-pre-line">{registro.nota}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {registro.autor_nombre} · {formatearFechaHora(registro.created_at)}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
          <h2 className="font-display text-lg">Historial de estatus</h2>
          <ul className="mt-4 space-y-3">
            {consulta.data?.historial.map((registro) => (
              <li key={registro.id} className="rounded-xl border border-border/70 p-3 text-sm">
                <p className="font-medium">
                  {ETIQUETA_ESTATUS_POSTULACION[registro.estatus_nuevo as EstatusPostulacion]}
                </p>
                {registro.comentario ? (
                  <p className="mt-1 text-muted-foreground">{registro.comentario}</p>
                ) : null}
                <p className="mt-1 text-xs text-muted-foreground">
                  {registro.autor_nombre} · {formatearFechaHora(registro.created_at)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string | null | undefined }) {
  return (
    <div className="rounded-lg border border-border/70 p-3">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{etiqueta}</dt>
      <dd className="mt-0.5 break-words font-medium">{valor || "—"}</dd>
    </div>
  );
}
